/**
 * entry-meta (§7.M4) — presentation-only derivations off a capture's working
 * envelope/payload bag, shared by the Daily Logs list (DailyLog) and the
 * the re-opened save screen (canon `saveFrom:"log"`). Never a new read axis, never
 * a seam field; absent/ill-typed values degrade to undefined and simply don't
 * render. No DOM here — pure derivation so both the list and the review screen
 * present an entry identically.
 */
import type { OutboxRecord } from "../outbox/types";

export function payloadStr(record: OutboxRecord, key: string): string | undefined {
  const v = record.envelope.payload[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}
export function roomOf(record: OutboxRecord): string | undefined {
  return payloadStr(record, "room") ?? payloadStr(record, "room_zone");
}
export function criticalityOf(record: OutboxRecord): string | undefined {
  return payloadStr(record, "criticality_tier");
}
export function decidesOf(record: OutboxRecord): string | undefined {
  return payloadStr(record, "decides");
}

/**
 * "Filed by Stephen · edited by Tom 14:32" — or `undefined` when there is
 * nothing honest to say.
 *
 * Only SERVER rows carry attribution. A row this device captured was written by
 * whoever is holding it, so a byline on those would read "filed by me" on every
 * one — noise, and it would make the log look busier the less it knows.
 *
 * The edit clause is the carrier's own timestamp, not the entry's: the entry
 * keeps the canonical's `ts` so a next-day correction can never move it between
 * reports, which means "edited" needs its own clock or it would claim the
 * correction happened when the entry was first filed.
 */
export function attributionOf(record: OutboxRecord): string | undefined {
  const filer = payloadStr(record, "author_label");
  const editor = payloadStr(record, "edited_by_label");
  if (!filer && !editor) return undefined;

  const parts: string[] = [];
  if (filer) parts.push(`Filed by ${filer}`);
  if (editor) {
    // The server suppresses a self-correction, so an editor here is always
    // someone OTHER than the filer.
    const at = payloadStr(record, "edited_ts");
    const when = at ? clockLabel(at) : undefined;
    parts.push(`edited by ${editor}${when ? ` ${when}` : ""}`);
  }
  return parts.join(" · ");
}

/** `HH:MM` in the reader's own timezone. */
function clockLabel(iso: string): string | undefined {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
/**
 * Is this row a decision? (The daily log's "Decisions" filter.)
 *
 * It read ONLY `payload.item_type` until 2026-07-28, and **nothing local has ever
 * written that key** — `commitRoutedCapture` doesn't, and `serverLogEntryAsRecord`
 * doesn't either. `item_type` is a field of the SERVER read models (the master-list
 * row and the folded `Decision`), so the filter was matching nothing at all: tapping
 * "Decisions" emptied the log every time, on every device, since it shipped.
 *
 * What a decision capture actually carries is its coordinator ROUTE
 * (`payload.route.skill === "decision_log_dayone"`, written by
 * `commitRoutedCapture` — §7.11). That is the local marker, and it is the one this
 * now reads.
 *
 * `item_type` is kept as a second check, not deleted: it is the right key for any
 * row that came from a server read model, and dropping it would break that case the
 * moment such a row reaches this list. Presentation-only either way — no new read
 * axis, no seam field (this module's own contract).
 */
export function isDecision(record: OutboxRecord): boolean {
  if (payloadStr(record, "item_type") === "decision") return true;
  const route = record.envelope.payload["route"];
  return (
    typeof route === "object" &&
    route !== null &&
    (route as { skill?: unknown }).skill === "decision_log_dayone"
  );
}

/** A decision raise carries enough route state to restore the complete Type UI. */
export function isDecisionRaise(record: OutboxRecord): boolean {
  const route = record.envelope.payload["route"];
  const op =
    typeof route === "object" && route !== null
      ? (route as { operation?: unknown; op?: unknown }).operation ??
        (route as { operation?: unknown; op?: unknown }).op
      : undefined;
  return (
    typeof route === "object" &&
    route !== null &&
    (route as { skill?: unknown }).skill === "decision_log_dayone" &&
    op === "raise"
  );
}

/** The Action mirror of `isDecision`, on the same two keys and for the same
 * reason: the re-opened save screen has to show the Type this entry ALREADY is.
 * Without it the toggle read blank on an entry that is on the Action List, which
 * says the opposite of the truth — the same misread `initialDest` had, one
 * control over (Red's remove/re-add confusion, 2026-07-31). */
export function isAction(record: OutboxRecord): boolean {
  if (payloadStr(record, "item_type") === "action") return true;
  const route = record.envelope.payload["route"];
  return (
    typeof route === "object" &&
    route !== null &&
    (route as { skill?: unknown }).skill === "master_action_list"
  );
}
const TIME_FMT = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
export function timeLabel(record: OutboxRecord): string {
  const d = new Date(record.envelope.ts || record.createdAt);
  return Number.isNaN(d.getTime()) ? "" : TIME_FMT.format(d);
}
export function photoCount(record: OutboxRecord): number {
  return record.envelope.media.filter((m) => m.role === "primary_photo").length;
}
export function isToday(record: OutboxRecord, now: Date = new Date()): boolean {
  const d = new Date(record.createdAt);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}
/** A capture that was pre-persisted for durability during the transcribe leg and
 * then never finished.
 *
 * `app.tsx` writes a minimal HELD record the moment transcribing starts — parts
 * plus `project_ref` and nothing else — so a hung transcribe cannot strand the
 * recording in volatile memory. Its own comment flagged the consequence as a
 * follow-up: abandoning the save screen leaves that row behind. Red hit it in the
 * field on 2026-07-29 by force-quitting on the save screen, where it showed up as
 * a blank entry titled "Field note".
 *
 * It is NOT junk — it still holds the audio. Hiding it would trade a confusing row
 * for a LOST capture, so it is marked instead.
 *
 * That marking used to live in the TITLE ("Unfinished — tap to finish"). It moved
 * to a pill on 2026-07-30 (Red): the title is for what the entry says, and a
 * status belongs where every other status on the row already is, beside
 * "Critical". Same signal, one word, and the title stops carrying an instruction.
 *
 * Identified by payload SHAPE rather than by "has no transcript": a photo-only
 * capture is legitimately saved with no transcript and would be misread by that
 * test. The pre-persist is the only record whose payload is exactly
 * `{project_ref}`. */
export function isUnfinishedCapture(record: OutboxRecord): boolean {
  if (record.state !== "held") return false;
  const keys = Object.keys(record.envelope.payload);
  return keys.length === 1 && keys[0] === "project_ref";
}

/** The entry's human title — the first thing Mark actually heard/knows about
 * it, degrading to the room, then a neutral label. */
export function titleOf(record: OutboxRecord): string {
  return (
    payloadStr(record, "description") ??
    payloadStr(record, "intent") ??
    payloadStr(record, "transcript")?.slice(0, 80) ??
    roomOf(record) ??
    "Field note"
  );
}
